import React, { useState } from 'react';
import { useCareerData } from '@/hooks/useCareerData';
import { CareerAnalysisResponse } from '@/services/careerService';
import { IoSparkles, IoBriefcase, IoArrowBack, IoBookmark, IoTrash, IoSchool, IoTrendingUp } from 'react-icons/io5';
import './CareerModule.css';

interface CareerModuleProps {
  className?: string;
}

const CareerModule: React.FC<CareerModuleProps> = ({ className }) => {
  const { 
    inquiries, 
    isLoading, 
    isAnalyzing, 
    isSaving, 
    isDeleting,
    analyzeCareer, 
    saveCareerInquiry, 
    deleteCareerInquiry 
  } = useCareerData();
  
  const [analysisResult, setAnalysisResult] = useState<CareerAnalysisResponse | null>(null);
  const [currentInquiry, setCurrentInquiry] = useState('');
  const [inquiry, setInquiry] = useState('');
  const [error, setError] = useState('');

  const handleAnalyze = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!inquiry.trim()) {
      setError('Please enter your career question or interest');
      return;
    }

    if (inquiry.trim().length < 5) {
      setError('Please provide more details about your career interest');
      return;
    }

    setError('');
    
    try {
      setCurrentInquiry(inquiry.trim());
      const result = await analyzeCareer({ inquiry: inquiry.trim() });
      setAnalysisResult(result);
    } catch (error) {
      console.error('CareerModule: Analysis failed:', error);
      setError('Failed to analyze career inquiry. Please try again.');
    }
  };

  const handleSaveResult = async () => {
    if (!analysisResult || !currentInquiry) return;

    try {
      await saveCareerInquiry({
        inquiry_text: currentInquiry,
        interest_category: analysisResult.interest_category,
        career_title: analysisResult.career_title,
        career_description: analysisResult.career_description,
        coursework_description: analysisResult.coursework_description,
        career_paths: analysisResult.career_paths
      });
      
      // Reset state after saving
      setAnalysisResult(null);
      setCurrentInquiry('');
      setInquiry('');
    } catch (error) {
      console.error('Failed to save career inquiry:', error);
    }
  };

  const handleBackToSearch = () => {
    setAnalysisResult(null);
    setCurrentInquiry('');
    setError('');
  };

  const handleDeleteInquiry = async (inquiryId: number) => {
    if (window.confirm('Are you sure you want to delete this career exploration?')) {
      try {
        await deleteCareerInquiry(inquiryId);
      } catch (error) {
        console.error('Failed to delete career inquiry:', error);
      }
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric'
    });
  };

  if (isLoading) {
    return (
      <div className="career-module-loading">
        <div className="career-loading-spinner"></div>
        <p>Loading career explorations...</p>
      </div>
    );
  }

  return (
    <div className={className}>
      {/* Input Box or Analysis Results */}
      {!analysisResult ? (
        <div className="career-input-section">
          <div className="career-input-header">
            <IoBriefcase className="career-input-icon" />
            <div>
              <h4 className="career-input-title">Explore Careers & Majors</h4>
              <p className="career-input-subtitle">Ask about any career or major you're interested in</p>
            </div>
          </div>

          <form onSubmit={handleAnalyze} className="career-input-form">
            <div className="career-input-container">
              <textarea
                value={inquiry}
                onChange={(e) => {
                  setInquiry(e.target.value);
                  if (error) setError('');
                }}
                placeholder="What is it like to work in software engineering?"
                className={`career-input-textarea ${error ? 'error' : ''}`}
                rows={3}
                maxLength={500}
                disabled={isAnalyzing}
              />
              <button
                type="submit"
                disabled={isAnalyzing || !inquiry.trim()}
                className="career-input-submit"
              >
                {isAnalyzing ? (
                  <div className="career-input-spinner" />
                ) : (
                  <IoSparkles />
                )}
              </button>
            </div>
            {error && <div className="career-input-error">{error}</div>}
          </form>
        </div>
      ) : (
        <div className="career-analysis-compact">
          <div className="career-analysis-header">
            <button onClick={handleBackToSearch} className="career-back-button">
              <IoArrowBack />
            </button>
            <div className="career-analysis-info">
              <h4 className="career-analysis-title">{analysisResult.career_title}</h4>
              <span className="career-analysis-category">{analysisResult.interest_category}</span>
            </div>
            <button
              onClick={handleSaveResult}
              disabled={isSaving}
              className="career-save-button"
            >
              {isSaving ? 'Saving...' : 'Save'}
            </button>
          </div>

          <div className="career-analysis-content">
            <div className="career-analysis-section">
              <p className="career-analysis-description">{analysisResult.career_description}</p>
            </div>

            <div className="career-analysis-section">
              <h5><IoSchool /> Coursework & Education</h5>
              <p>{analysisResult.coursework_description}</p>
            </div>

            {analysisResult.career_paths && analysisResult.career_paths.length > 0 && (
              <div className="career-analysis-section">
                <h5><IoTrendingUp /> Career Paths</h5>
                <div className="career-paths-compact">
                  {analysisResult.career_paths.map((path, index) => (
                    <div key={index} className="career-path-compact">
                      <h6>{path.title}</h6>
                      <p>{path.description}</p>
                      {path.suggested_majors && path.suggested_majors.length > 0 && (
                        <div className="career-path-tags">
                          <strong>Majors:</strong>
                          {path.suggested_majors.map((major, i) => (
                            <span key={i} className="career-tag major">{major}</span>
                          ))}
                        </div>
                      )}
                      {path.required_skills && path.required_skills.length > 0 && (
                        <div className="career-path-tags">
                          <strong>Skills:</strong>
                          {path.required_skills.map((skill, i) => (
                            <span key={i} className="career-tag skill">{skill}</span>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Compact Saved Explorations */}
      {inquiries.length > 0 && (
        <div className="career-saved-section">
          <h4 className="career-saved-title">
            <IoBookmark /> Your Explorations ({inquiries.length})
          </h4>
          <div className="career-saved-list">
            {inquiries.map((inquiry) => (
              <div key={inquiry.id} className="career-saved-item">
                <div className="career-saved-content">
                  <div className="career-saved-single-line">
                    <span className="career-saved-inquiry">"{inquiry.inquiry_text}"</span>
                    <span className="career-saved-separator">•</span>
                    <span className="career-saved-category">{inquiry.interest_category}</span>
                    <span className="career-saved-separator">•</span>
                    <span className="career-saved-date">{formatDate(inquiry.created_at)}</span>
                    {inquiry.career_paths && inquiry.career_paths.length > 0 && (
                      <>
                        <span className="career-saved-separator">•</span>
                        <span className="career-saved-paths">{inquiry.career_paths.length} paths</span>
                      </>
                    )}
                  </div>
                </div>
                <button
                  onClick={() => handleDeleteInquiry(inquiry.id)}
                  disabled={isDeleting}
                  className="career-saved-delete"
                  title="Delete exploration"
                >
                  <IoTrash />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default CareerModule;